import type React from "react";
import { AnimatePresence, motion } from "motion/react";
import { Slot } from "@radix-ui/react-slot";
import { getVariant } from "./variants";
import type { AnimateComponent, AnimateProps } from "./types";
import { splitMotionProps, createMotionComponent } from "./lib";

function Base<T extends React.ElementType = "div">({
  asChild,
  children,
  as: Tag,
  disable = false,
  ...props
}: AnimateProps<T>) {
  const { motionProps, domProps } =
    splitMotionProps<
      Omit<AnimateProps<T>, "asChild" | "children" | "as" | "disable">
    >(props);
  const MotionTag = createMotionComponent(Tag);
  const Comp = asChild ? motion.create(Slot) : MotionTag;

  return (
    <Comp {...(disable ? {} : motionProps)} {...domProps}>
      {children}
    </Comp>
  );
}

function Wrapper<T extends React.ElementType = "div">({
  asChild,
  children,
  as: Tag,
  disable = false,
  ...props
}: AnimateProps<T>) {
  const { motionProps, domProps } = splitMotionProps(props);
  const MotionTag = createMotionComponent(Tag);
  const Comp = asChild ? motion.create(Slot) : MotionTag;

  return (
    <AnimatePresence>
      <Comp {...(disable ? {} : motionProps)} {...domProps}>
        {children}
      </Comp>
    </AnimatePresence>
  );
}

function Parent<T extends React.ElementType = "div">({
  children,
  asChild,
  variant = "fadeIn",
  as: Tag,
  disable = false,
  ...props
}: AnimateProps<T>) {
  const { motionProps, domProps } = splitMotionProps(props);
  const MotionTag = createMotionComponent(Tag);
  const Comp = asChild ? motion.create(Slot) : MotionTag;

  return (
    <Comp
      variants={getVariant(variant)}
      {...(disable ? {} : motionProps)}
      {...domProps}
    >
      {children}
    </Comp>
  );
}

function Child<T extends React.ElementType = "div">({
  children,
  asChild,
  as: Tag,
  variant = "fadeIn",
  disable,
  ...props
}: AnimateProps<T>) {
  const { motionProps, domProps } = splitMotionProps(props);
  const MotionTag = createMotionComponent(Tag);
  const Comp = asChild ? motion.create(Slot) : MotionTag;

  return (
    <Comp
      variants={getVariant(variant)}
      {...(disable ? {} : motionProps)}
      {...domProps}
    >
      {children}
    </Comp>
  );
}

const AnimateBase = Base as AnimateComponent;
const AnimateWrapper = Wrapper as AnimateComponent;
const AnimateParent = Parent as AnimateComponent;
const AnimateChild = Child as AnimateComponent;

const Animate = Object.assign(AnimateBase, {
  Wrapper: AnimateWrapper,
  Parent: AnimateParent,
  Child: AnimateChild,
});

export default Animate;
